import { z } from 'zod';

const gitRefSchema = z.string().min(1).max(256);

export const gitChangesArtifactContentSchema = z
  .object({
    repo: z.string().min(1),
    /** Older / starting commit or ref. Must be paired with `head`. */
    base: gitRefSchema.optional(),
    /** Newer / ending commit or ref. Must be paired with `base`. */
    head: gitRefSchema.optional(),
  })
  .refine((value) => (value.base === undefined) === (value.head === undefined), {
    message: 'base and head must both be provided or both omitted',
  });

export type GitChangesArtifactContent = z.infer<typeof gitChangesArtifactContentSchema>;

/**
 * Parses line-based `key: value` artifact body into a plain object.
 * @param content Trimmed artifact body text.
 * @returns Key-value map from non-empty lines with a colon.
 */
function parseLineBasedContent(content: string): Record<string, string> {
  const meta: Record<string, string> = {};
  for (const line of content.split('\n')) {
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) {
      continue;
    }
    const key = line.slice(0, colonIndex).trim();
    const value = line.slice(colonIndex + 1).trim();
    if (key.length > 0) {
      meta[key] = value;
    }
  }
  return meta;
}

/**
 * Shortens a full SHA for preview labels; leaves branch/tag refs unchanged.
 * @param ref Commit hash or symbolic ref.
 */
function shortenGitRef(ref: string): string {
  if (/^[0-9a-f]{7,40}$/i.test(ref)) {
    return ref.slice(0, 7);
  }
  return ref;
}

/**
 * Parses git-changes artifact content from JSON or YAML-style key-value lines.
 * @param content Raw artifact body.
 * @returns Parsed pointer fields or null when invalid.
 */
export function parseGitChangesArtifactContent(content: string): GitChangesArtifactContent | null {
  const trimmed = content.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const json: unknown = JSON.parse(trimmed);
    const jsonResult = gitChangesArtifactContentSchema.safeParse(json);
    if (jsonResult.success) {
      return jsonResult.data;
    }
  } catch {
    // fall through to line-based parsing
  }

  const lineResult = gitChangesArtifactContentSchema.safeParse(parseLineBasedContent(trimmed));
  return lineResult.success ? lineResult.data : null;
}

/**
 * Builds a short preview label for git-changes artifact cards.
 * @param parsed Validated git-changes pointer content.
 * @returns Preview string such as the repo folder name, optionally with a commit range.
 */
export function formatGitChangesArtifactPreview(parsed: GitChangesArtifactContent): string {
  if (parsed.base !== undefined && parsed.head !== undefined) {
    return `${parsed.repo} (${shortenGitRef(parsed.base)}…${shortenGitRef(parsed.head)})`;
  }
  return parsed.repo;
}
